/*
* <license header>
*/

import React, { useState } from 'react'
import { Button, Flex, Heading, Text, View } from '@adobe/react-spectrum'
import { PageHeaderBanner } from './shared/PageHeaderBanner'
import { APP_TYPES } from '../data/constants'
import FileTemplate from '@spectrum-icons/workflow/FileTemplate'
import Folder from '@spectrum-icons/workflow/Folder'
import Starburst from '@spectrum-icons/workflow/Starburst'
import Compass from '@spectrum-icons/workflow/Compass'
import Code from '@spectrum-icons/workflow/Code'
import Data from '@spectrum-icons/workflow/Data'
import ChevronDown from '@spectrum-icons/workflow/ChevronDown'
import ChevronUp from '@spectrum-icons/workflow/ChevronUp'
import Trophy from '@spectrum-icons/workflow/Trophy'
import Servers from '@spectrum-icons/workflow/Servers'

const CARD_STYLE = {
  border: '1px solid var(--card-border)',
  borderRadius: '8px',
  padding: '1.25rem',
  backgroundColor: 'var(--white)'
}

const LABEL_BASE_STYLE = {
  display: 'inline-block',
  padding: '0.25rem 0.5rem',
  borderRadius: '4px',
  color: 'white',
  fontSize: '0.75rem',
  fontWeight: 600,
  marginBottom: '0.5rem'
}

const SUB_BUBBLE_STYLE = {
  border: '1px solid var(--gray-300)',
  borderRadius: '6px',
  padding: '0.75rem 1rem',
  backgroundColor: 'var(--gray-100)',
  marginTop: '0.5rem'
}

function SubBubble ({ subPhase, alignRight }) {
  return (
    <div
      className="Roadmap-subBubble"
      style={{
        ...SUB_BUBBLE_STYLE,
        ...(alignRight && { textAlign: 'right' })
      }}
    >
      <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--gray-900)', marginBottom: '0.25rem' }}>
        {subPhase.title}
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', lineHeight: 1.4 }}>
        {subPhase.description}
      </div>
    </div>
  )
}

function PhaseBubble ({ phase, alignRight }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasSubPhases = phase.subPhases?.length > 0
  const IconComponent = phase.icon
  const bubbleStyle = {
    ...CARD_STYLE,
    ...(alignRight && {
      textAlign: 'right'
    })
  }
  const cardContent = (
    <>
      <div style={alignRight ? { display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' } : undefined}>
        <span style={{ ...LABEL_BASE_STYLE, backgroundColor: phase.tagColor ?? 'var(--card-accent)' }}>{phase.label}</span>
      </div>
      <Flex
        alignItems="center"
        gap="size-150"
        marginBottom="size-200"
        direction={alignRight ? 'row-reverse' : 'row'}
        UNSAFE_style={alignRight ? { justifyContent: 'flex-end' } : undefined}
      >
        <IconComponent size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={3} UNSAFE_style={{ margin: 0, fontSize: '1rem', fontWeight: 600, ...(alignRight && { textAlign: 'right' }) }}>
          {phase.title}
        </Heading>
      </Flex>
      <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.5, ...(alignRight && { textAlign: 'right' }) }}>
        {phase.description}
      </Text>
      {hasSubPhases && (
        <>
          <Flex
            marginTop="size-200"
            justifyContent={alignRight ? 'flex-end' : 'start'}
          >
            <Button
              variant="primary"
              onPress={() => setIsExpanded((v) => !v)}
              aria-expanded={isExpanded}
              UNSAFE_style={{
                backgroundColor: '#1877F2',
                border: 'none',
                borderRadius: '9999px',
                padding: '0.5rem 1rem',
                color: 'white',
                fontWeight: 500,
                boxShadow: 'none'
              }}
            >
              {isExpanded ? (
                <Flex alignItems="center" gap="size-100" direction={alignRight ? 'row-reverse' : 'row'}>
                  <ChevronUp size="XS" UNSAFE_style={{ color: 'white' }} />
                  <span>Hide details</span>
                </Flex>
              ) : (
                <Flex alignItems="center" gap="size-100" direction={alignRight ? 'row-reverse' : 'row'}>
                  <ChevronDown size="XS" UNSAFE_style={{ color: 'white' }} />
                  <span>Show details</span>
                </Flex>
              )}
            </Button>
          </Flex>
          {isExpanded && phase.subPhases.map((subPhase) => (
            <SubBubble key={subPhase.id} subPhase={subPhase} alignRight={alignRight} />
          ))}
        </>
      )}
    </>
  )

  return (
    <View
      UNSAFE_style={bubbleStyle}
      className="Roadmap-phaseBubble"
    >
      {alignRight ? (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {cardContent}
        </div>
      ) : (
        cardContent
      )}
    </View>
  )
}

const ROADMAP_PHASES = [
  {
    id: 'phase-0',
    label: 'Milestone',
    title: 'EA Onsite 2026 AI Challenge',
    icon: Trophy,
    tagColor: '#d4a017',
    description: 'Presenting AppBuilder Factory in a science-fair style at the EA Onsite 2026 AI Challenge. Winning will fund the project for full implementation.'
  },
  {
    id: 'phase-1',
    label: 'Capability',
    title: 'App Management',
    tagColor: '#4caf50',
    icon: Folder,
    description: 'Enable browsing, managing, and onboarding existing Adobe App Builder applications. Track status, organize your portfolio, and centralize app lifecycle across the Adobe Experience Cloud ecosystem.',
    subPhases: [
      {
        id: 'phase-1a',
        title: 'App list & browse',
        description: 'List apps from Adobe Developer Console and GitHub. Filter by status, type, org. Search and sort.'
      },
      {
        id: 'phase-1b',
        title: 'Status tracking',
        description: 'Track deployment status (staging, production), build state, and health. Surface errors and sync state.'
      },
      {
        id: 'phase-1c',
        title: 'Onboard existing app',
        description: 'Import apps by GitHub URL or Adobe project. Detect type, validate structure, register in repository.'
      },
      {
        id: 'phase-1d',
        title: 'Portfolio organization',
        description: 'Group apps by project, workspace, or custom tags. Manage lifecycle across Adobe Experience Cloud.'
      }
    ]
  },
  {
    id: 'phase-2',
    label: 'Capability',
    title: 'Assistant with RAG',
    icon: Starburst,
    tagColor: '#4caf50',
    description: 'AI-powered assistant with Retrieval-Augmented Generation over Adobe Developer Console, Extensions, AppBuilder Factory, and Spectrum documentation. Get contextual guidance for building apps, configuring extensions, and integrating with Adobe services.',
    subPhases: [
      {
        id: 'phase-2a',
        title: 'RAG ingestion',
        description: 'Ingest and index documentation from Adobe Developer Console, Extensions, AppBuilder Factory, and Spectrum. Chunk, embed, and store for retrieval.'
      },
      {
        id: 'phase-2b',
        title: 'MCP Client & conversations',
        description: 'MCP Client receives assistant conversations. Manage chat history, context window, and session state.'
      },
      {
        id: 'phase-2c',
        title: 'Context retrieval & query',
        description: 'Retrieve relevant docs for user queries. Rank and inject context into prompts. Support multi-turn conversations.'
      },
      {
        id: 'phase-2d',
        title: 'Response generation',
        description: 'LLM generates guidance, code snippets, and recommendations. Stream responses. Handle follow-up questions.'
      }
    ]
  },
  {
    id: 'phase-2b',
    label: 'Capability',
    title: 'AppBuilder Factory MCP Server',
    icon: Servers,
    tagColor: '#4caf50',
    description: 'Develop the AppBuilder Factory MCP Server that manages AI assistant conversations and exposes tools for the development layer. Enables Cursor IDE integration and agentic workflows.',
    subPhases: [
      {
        id: 'phase-2b-a',
        title: 'MCP Server core',
        description: 'Implement Model Context Protocol server. Expose tools for app management, repository operations, and Adobe Console integration.'
      },
      {
        id: 'phase-2b-b',
        title: 'Assistant conversation handling',
        description: 'Manage AI assistant conversations, session state, and context. Bridge between MCP Client and application actions.'
      },
      {
        id: 'phase-2b-c',
        title: 'Development layer tools',
        description: 'Tools for checkout, create, deploy, check-in. GitHub and Adobe I/O Runtime integration. Support for vibe-coding actions.'
      },
      {
        id: 'phase-2b-d',
        title: 'Dev container & Cursor integration',
        description: 'Package MCP Server for dev containers. Cursor IDE discovery and connection. Agentic development best practices.'
      }
    ]
  },
  {
    id: 'phase-3',
    label: 'Capability',
    title: 'Agentic Development',
    icon: Code,
    tagColor: '#4caf50',
    description: 'Enable agentic development with Cursor IDE. Dev containers with git, Adobe I/O CLI, and MCP Server let technical users and dev agents work in a unified environment. Checkout, develop, and check-in to Adobe I/O Customer ORG projects with staging and production workspaces.',
    subPhases: [
      {
        id: 'phase-3a',
        title: 'Dev container setup',
        description: 'Provision dev containers with git, Adobe I/O CLI, and MCP Server. Pre-configured for Cursor IDE and agentic workflows.'
      },
      {
        id: 'phase-3b',
        title: 'Cursor IDE integration',
        description: 'MCP Server in container for Cursor. Tools for checkout, create, deploy, check-in. Agentic development best practices.'
      },
      {
        id: 'phase-3c',
        title: 'Checkout flow',
        description: 'Clone AppBuilder Factory or app repo to Customer Instance. Link to Adobe project. Ready for development.'
      },
      {
        id: 'phase-3d',
        title: 'Check-in flow',
        description: 'Commit, push, deploy to staging and production workspaces. Sync with Adobe I/O Customer ORG projects.'
      }
    ]
  },
  {
    id: 'phase-4',
    label: 'Capability',
    title: 'Vibe Coding',
    icon: Data,
    tagColor: '#4caf50',
    description: 'LLM Engine generates vibe-coding actions that deploy to the Customer Instance dev container. MCP Client receives assistant conversations and performs actions. Agentic workflows from natural language to deployed code.',
    subPhases: [
      {
        id: 'phase-4a',
        title: 'LLM Engine',
        description: 'Generate vibe-coding actions from natural language. Code generation, file edits, config changes. Structured output for actions.'
      },
      {
        id: 'phase-4b',
        title: 'MCP Client action execution',
        description: 'MCP Client receives assistant intents and invokes tools. Create files, run CLI commands, deploy actions.'
      },
      {
        id: 'phase-4c',
        title: 'Deploy to Customer Instance',
        description: 'Deploy generated code to dev container. Build, test, deploy to Adobe I/O Runtime. Feedback loop for user.'
      },
      {
        id: 'phase-4d',
        title: 'Natural language to code',
        description: 'End-to-end workflow: user describes intent, LLM generates actions, MCP executes, code deploys. Iterate with follow-ups.'
      }
    ]
  },
  {
    id: 'phase-5',
    label: `${APP_TYPES.length} App Types`,
    title: 'App Type Support',
    icon: FileTemplate,
    tagColor: '#0891b2',
    description: 'Add support for each Adobe App Builder type: Unified Shell, GenStudio extensions, APIs, API Mesh, Workfront extensions, AEM extensions, and more. Extend the platform to cover the full range of App Builder use cases.',
    subPhases: [
      {
        id: 'phase-5a',
        title: 'Scaffolding & generator integration',
        description: 'Wire each app type to its npm generator package (e.g., @adobe/generator-app-excshell). Scaffold project structure, config, and dependencies per type.'
      },
      {
        id: 'phase-5b',
        title: 'Create wizard per type',
        description: 'Type-specific create flow: Adobe Console project, workspace, GitHub repo. Parameters and validation per type.'
      },
      {
        id: 'phase-5c',
        title: 'Onboarding & validation',
        description: 'Onboard existing apps by type. Validate structure, detect type, register in app repository.'
      },
      {
        id: 'phase-5d',
        title: 'Deployment & status',
        description: 'Deploy actions per type. Track deployment status, surface type-specific errors.'
      }
    ]
  },
  {
    id: 'phase-6',
    label: 'Templates',
    title: 'Application Templates',
    icon: FileTemplate,
    tagColor: '#0891b2',
    description: 'Add pre-built application templates and real-world examples. Bootstrap projects from proven structures—integrations, extensions, dashboards—and customize to your needs. Scale the library as the platform matures.',
    subPhases: [
      {
        id: 'phase-6a',
        title: 'Template catalog & metadata',
        description: 'Catalog template slugs, metadata, use cases, tech stack. Search and filter by type and use case.'
      },
      {
        id: 'phase-6b',
        title: 'One-click clone & deploy',
        description: 'Clone from GitHub, create Adobe project/workspace, link repo. Deploy to staging.'
      },
      {
        id: 'phase-6c',
        title: 'Customization wizard',
        description: 'Post-clone setup: env vars, config, branding. Guided steps per template.'
      },
      {
        id: 'phase-6d',
        title: 'Integration & extension templates',
        description: 'Event-driven integrations (e.g., Push Assets). Shell extensions (GenStudio Certification, Operational Dashboard). API templates.'
      },
      {
        id: 'phase-6e',
        title: 'Versioning & updates',
        description: 'Track template versions. Notify and support upgrades from older templates.'
      }
    ]
  }
]

export const Roadmap = () => (
  <View width="100%" minWidth={0} padding="size-400" UNSAFE_style={{ paddingBottom: '2.5rem' }}>
    <PageHeaderBanner
      title="AppBuilder Factory Roadmap"
      subtitle="Our planned phases and milestones for AppBuilder Factory capabilities"
    />

    <View
      UNSAFE_style={CARD_STYLE}
      marginBottom="size-300"
    >
      <Flex alignItems="center" gap="size-150" marginBottom="size-200">
        <Compass size="S" UNSAFE_style={{ color: 'var(--card-accent)', flexShrink: 0 }} />
        <Heading level={2} UNSAFE_style={{ margin: 0, fontSize: '1.125rem' }}>
          Our Roadmap
        </Heading>
      </Flex>
      <Text UNSAFE_style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.6 }}>
        We are building AppBuilder Factory capabilities first—<strong>app management</strong>, <strong>assistant with RAG</strong>,{' '}
        <strong>AppBuilder Factory MCP Server</strong>, <strong>agentic development with Cursor</strong>, and <strong>vibe coding</strong>—then scaling out with{' '}
        <strong>app type support</strong> and <strong>templates</strong>. The timeline below outlines our planned milestones and priorities.
      </Text>
    </View>

    <div className="Roadmap-timeline Roadmap-timeline--centered">
      {ROADMAP_PHASES.map((phase, index) => (
        <div className="Roadmap-timelineRow" key={phase.id}>
          <div className="Roadmap-timelineContent Roadmap-timelineContent--left">
            {index % 2 === 0 ? (
              <PhaseBubble phase={phase} alignRight />
            ) : null}
          </div>
          <div className="Roadmap-timelineSpine">
            <div className="Roadmap-timelineDot" aria-hidden="true" />
            {index < ROADMAP_PHASES.length - 1 && (
              <div className="Roadmap-timelineLine" aria-hidden="true" />
            )}
          </div>
          <div className="Roadmap-timelineContent Roadmap-timelineContent--right">
            {index % 2 === 1 ? (
              <PhaseBubble phase={phase} />
            ) : null}
          </div>
        </div>
      ))}
    </div>
  </View>
)
